import { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Select } from '@components/Select';
import { Input } from '@components/Input';
import ComboLocationSelect from '@features/ComboLocationSelect';
import { useTranslation, useLocations } from '@hooks';
import { channelsActions } from '@actions';

const Container = styled('div')({
    display: 'grid',
    gridGap: '16px 24px',
    gridTemplateAreas: '"input1" "select1" "select2"',
    gridTemplateColumns: '100%',
    '@media (max-width: 600px)': {
        gridTemplateAreas: '"input1" "select1" "select2"',
        gridGap: '16px',
    },
    '& .combo-location-select': {
        gridArea: 'select2',
        '& .location-type': {
            marginTop: '-16px',
        },
    },
});

const WhatsAppBusinessForm = ({ isEdit }) => {
    const brands = useSelector(state => state.brands.data);
    const { whatsAppBusinessPhoneNumbers, whatsAppBusinessPhoneNumbersLoading } = useSelector(state => state.channels);

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { getSelectLocationsOptions } = useLocations();
    const { control, setValue, selectedChannel } = useFormContext();

    useEffect(() => {
        if (!isEdit) {
            dispatch(channelsActions.getWhatsAppBusinessPhoneNumbers());
        }
    }, []);

    const brandsOptions = useMemo(() => brands.map(item => ({ value: item.id, label: item.title })), [brands]);

    const locationsOptions = useMemo(() => getSelectLocationsOptions(), []);

    const phoneNumberOptions = useMemo(() => {
        return whatsAppBusinessPhoneNumbers
            .filter(item => item.connectedChannelId === null)
            .map(item => ({ value: item.id, label: item.phoneNumber }));
    }, [whatsAppBusinessPhoneNumbers]);

    return (
        <Container>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.channelName')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                        sx={{ gridArea: 'input1' }}
                    />
                )}
            />
            {isEdit ? (
                <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.phoneNumber')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            disabled={isEdit}
                        />
                    )}
                />
            ) : (
                <Controller
                    control={control}
                    name="phoneNumberId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            onChange={({ target: { value } }) => {
                                const phoneObject = whatsAppBusinessPhoneNumbers.find(item => item.id === value);
                                setValue('phoneNumberId', value, { shouldDirty: true, shouldValidate: true });
                                setValue('phoneNumberName', phoneObject?.name, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                setValue('whatsAppBusinessId', phoneObject.whatsAppBusinessId, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                setValue('phoneNumber', phoneObject.phoneNumber, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                            }}
                            options={phoneNumberOptions}
                            label={t('common.phoneNumber')}
                            fullWidth
                            required
                            disabled={isEdit || whatsAppBusinessPhoneNumbersLoading}
                            error={!!error}
                        />
                    )}
                />
            )}
            <ComboLocationSelect
                brandsOptions={brandsOptions}
                locationsOptions={locationsOptions}
                data={selectedChannel}
            />
        </Container>
    );
};

export default WhatsAppBusinessForm;
